// Content Versions Module - API service for Inline AI Editor
// Manages AI-generated content versions for job fields

import { BaseModule } from './baseModule';
import type { AxiosInstance } from 'axios';
import type { ErrorHandler } from './errorHandler';

// ============================================================================
// Types
// ============================================================================

export type ContentComponentType = 'title' | 'summary' | 'description' | 'requirements' | 'benefits' | 'image';

export interface ContentVersion {
  id: string;
  job_id: string;
  component_type: string;
  content: string;
  prompt_used: string | null;
  is_active: number;
  version_number: number;
  created_by: string | null;
  created_at: string;
}

export interface ContentVersionsResponse {
  active: ContentVersion | null;
  history: ContentVersion[];
  total: number;
}

export interface GenerateContentRequest {
  prompt?: string;
  tone?: 'professional' | 'casual' | 'technical';
}

export interface GenerateContentResponse {
  version: ContentVersion;
}

export interface ActivateVersionResponse {
  success: boolean;
  version: ContentVersion;
}

export interface DeleteVersionResponse {
  success: boolean;
}

// ============================================================================
// Module Class
// ============================================================================

export class ContentVersionsModule extends BaseModule {
  constructor(client: AxiosInstance, errorHandler: ErrorHandler) {
    super(client, errorHandler);
  }

  /**
   * Get all versions for a job component
   */
  async getVersions(
    jobId: string,
    componentType: ContentComponentType
  ): Promise<ContentVersionsResponse> {
    return this.get<ContentVersionsResponse>(
      `/api/admin/jobs/${jobId}/content/${componentType}/versions`
    );
  }

  /**
   * Generate new content using AI
   */
  async generate(
    jobId: string,
    componentType: ContentComponentType,
    request: GenerateContentRequest = {}
  ): Promise<GenerateContentResponse> {
    return this.post<GenerateContentResponse>(
      `/api/admin/jobs/${jobId}/content/${componentType}/generate`,
      request
    );
  }

  /**
   * Activate a specific version
   */
  async activate(
    jobId: string,
    componentType: ContentComponentType,
    versionId: string
  ): Promise<ActivateVersionResponse> {
    return this.post<ActivateVersionResponse>(
      `/api/admin/jobs/${jobId}/content/${componentType}/versions/${versionId}/activate`,
      {}
    );
  }

  /**
   * Delete a specific version (cannot delete active version)
   */
  async deleteVersion(
    jobId: string,
    componentType: ContentComponentType,
    versionId: string
  ): Promise<DeleteVersionResponse> {
    return this.delete<DeleteVersionResponse>(
      `/api/admin/jobs/${jobId}/content/${componentType}/versions/${versionId}`
    );
  }
}
